// src/components/admin/settings/AdminSettings.jsx
import React, { useEffect, useState } from "react";
import ConfirmDialog from "../../common/ConfirmDialog";
import { ADMIN_MOCK_USERS } from "@/utils/constants";

/**
 * AdminSettings.jsx
 * Frontend-only admin settings page (uses local state + localStorage)
 *
 * - Drop this file in src/components/admin/settings/AdminSettings.jsx
 * - Add route: <Route path="settings" element={<AdminSettings />} />
 *
 * Persistence:
 * - Uses localStorage key "koset_admin_settings" for dev persistence.
 *
 * Note: This is a frontend mock. Replace the save handlers with real API calls
 * once the backend endpoints are ready (e.g. PATCH /admin/settings).
 */

const STORAGE_KEY = "koset_admin_settings_v1";

const defaultValues = {
  maintenanceMode: false,
  featureFlags: {
    newRouteEditor: false,
    experimentalMap: false,
    emailReminders: true,
  },
  maxGpxUploadMb: 10,
  admins: ADMIN_MOCK_USERS.slice(0, 3).map((u, i) => ({
    id: u.id,
    name: u.name,
    email: u.email,
    role: i === 0 ? "owner" : "admin", // first is owner
  })),
  emailTemplates: {
    routeApproved:
      'Hi {{name}},\n\nYour route "{{route_name}}" has been approved. Thanks!\n\n— Team Koset',
    eventReminder:
      "Hi {{name}},\n\nReminder: {{event_title}} is happening on {{date}}. See you there!\n\n— Koset",
  },
};

function loadSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultValues;
    return { ...defaultValues, ...JSON.parse(raw) };
  } catch (e) {
    console.warn("Failed reading admin settings from localStorage:", e);
    return defaultValues;
  }
}

function saveSettings(payload) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (e) {
    console.warn("Failed saving admin settings to localStorage:", e);
  }
}

export default function AdminSettings() {
  const [settings, setSettings] = useState(loadSettings());
  const [dirty, setDirty] = useState(false);

  // confirm dialog state for admin revoke
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toRevoke, setToRevoke] = useState(null);

  // convenience local edits
  useEffect(() => {
    // ensure defaults are present
    setSettings((s) => ({ ...defaultValues, ...s }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setMaintenance = (v) => {
    setSettings((s) => ({ ...s, maintenanceMode: v }));
    setDirty(true);
  };

  const toggleFeature = (key) => {
    setSettings((s) => ({
      ...s,
      featureFlags: { ...s.featureFlags, [key]: !s.featureFlags[key] },
    }));
    setDirty(true);
  };

  const setMaxGpx = (val) => {
    const n = Number(val) || 1;
    setSettings((s) => ({ ...s, maxGpxUploadMb: n }));
    setDirty(true);
  };

  const changeAdminRole = (id, role) => {
    setSettings((s) => ({
      ...s,
      admins: s.admins.map((a) => (a.id === id ? { ...a, role } : a)),
    }));
    setDirty(true);
  };

  const requestRevoke = (admin) => {
    setToRevoke(admin);
    setConfirmOpen(true);
  };

  const confirmRevoke = () => {
    if (!toRevoke) return;
    setSettings((s) => ({
      ...s,
      admins: s.admins.filter((a) => a.id !== toRevoke.id),
    }));
    setToRevoke(null);
    setConfirmOpen(false);
    setDirty(true);
  };

  const cancelRevoke = () => {
    setToRevoke(null);
    setConfirmOpen(false);
  };

  const updateEmailTemplate = (key, value) => {
    setSettings((s) => ({
      ...s,
      emailTemplates: { ...s.emailTemplates, [key]: value },
    }));
    setDirty(true);
  };

  const handleSaveAll = () => {
    saveSettings(settings);
    setDirty(false);
    // show basic feedback (toast would be better in real app)
    alert("Settings saved locally.");
  };

  const handleReset = () => {
    if (!window.confirm("Reset admin settings to defaults?")) return;
    setSettings(defaultValues);
    saveSettings(defaultValues);
    setDirty(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
          Admin Settings
        </h2>

        <div className="flex items-center gap-3">
          <button
            className="px-3 py-2 rounded bg-gray-100 dark:bg-gray-700 text-sm"
            onClick={handleReset}
          >
            Reset to defaults
          </button>

          <button
            onClick={handleSaveAll}
            disabled={!dirty}
            className={`px-3 py-2 rounded text-sm ${
              dirty
                ? "bg-green-600 text-white hover:bg-green-700"
                : "bg-gray-200 text-gray-500 cursor-not-allowed"
            }`}
          >
            Save changes
          </button>
        </div>
      </div>

      {/* Maintenance */}
      <section className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="font-medium text-lg mb-2">Site status</h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Toggle maintenance mode to temporarily hide the public site from
          visitors.
        </p>

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings.maintenanceMode}
              onChange={(e) => setMaintenance(e.target.checked)}
            />
            <span className="text-sm">Maintenance mode</span>
          </label>

          <div className="text-sm text-gray-500">
            Current:{" "}
            <strong>{settings.maintenanceMode ? "Enabled" : "Disabled"}</strong>
          </div>
        </div>
      </section>

      {/* Feature Flags */}
      <section className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="font-medium text-lg mb-2">Feature flags</h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Flip feature flags to enable/disable experimental features for
          testing.
        </p>

        <div className="space-y-3">
          {Object.entries(settings.featureFlags).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between">
              <div className="capitalize text-sm">{k}</div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={v}
                  onChange={() => toggleFeature(k)}
                />
              </label>
            </div>
          ))}
        </div>
      </section>

      {/* Upload size and admin accounts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="font-medium text-lg mb-2">Uploads & quotas</h3>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            Configure the maximum allowed GPX upload size (MB).
          </p>

          <div className="flex items-center gap-3">
            <input
              type="number"
              min={1}
              value={settings.maxGpxUploadMb}
              onChange={(e) => setMaxGpx(e.target.value)}
              className="w-28 px-3 py-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
            />
            <div className="text-sm text-gray-600">MB</div>
          </div>
        </section>

        <section className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="font-medium text-lg mb-2">Admin accounts</h3>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            Manage admin users and roles (owner / admin / moderator).
          </p>

          <div className="space-y-3">
            {settings.admins.map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between border rounded px-3 py-2"
              >
                <div>
                  <div className="font-medium">{a.name}</div>
                  <div className="text-xs text-gray-500">{a.email}</div>
                </div>

                <div className="flex items-center gap-3">
                  <select
                    value={a.role}
                    onChange={(e) => changeAdminRole(a.id, e.target.value)}
                    className="px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
                  >
                    <option value="owner">Owner</option>
                    <option value="admin">Admin</option>
                    <option value="moderator">Moderator</option>
                  </select>

                  <button
                    onClick={() => requestRevoke(a)}
                    className="px-2 py-1 rounded bg-red-50 text-red-600 text-sm"
                    title="Revoke admin"
                  >
                    Revoke
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Email templates */}
      <section className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="font-medium text-lg mb-2">Email templates</h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Edit templates used for automated emails. Use{" "}
          <code>{"{{name}}"}</code> and other placeholders.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-gray-500 mb-2">Route approved</div>
            <textarea
              value={settings.emailTemplates.routeApproved}
              onChange={(e) =>
                updateEmailTemplate("routeApproved", e.target.value)
              }
              className="w-full h-40 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
            />
            <div className="text-xs text-gray-500 mt-2">Preview:</div>
            <div className="whitespace-pre-wrap bg-gray-50 dark:bg-gray-900 p-3 rounded mt-1 text-sm text-gray-700 dark:text-gray-200">
              {settings.emailTemplates.routeApproved}
            </div>
          </div>

          <div>
            <div className="text-sm text-gray-500 mb-2">Event reminder</div>
            <textarea
              value={settings.emailTemplates.eventReminder}
              onChange={(e) =>
                updateEmailTemplate("eventReminder", e.target.value)
              }
              className="w-full h-40 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
            />
            <div className="text-xs text-gray-500 mt-2">Preview:</div>
            <div className="whitespace-pre-wrap bg-gray-50 dark:bg-gray-900 p-3 rounded mt-1 text-sm text-gray-700 dark:text-gray-200">
              {settings.emailTemplates.eventReminder}
            </div>
          </div>
        </div>
      </section>

      {/* Developer Tools */}
      <section className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="font-medium text-lg mb-2">Developer Tools</h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Access developer utilities and component testing tools.
        </p>

        <div className="flex items-center gap-3">
          <a
            href="/ui-playground"
            className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 transition-colors text-sm font-medium inline-block"
          >
            Open UI Playground
          </a>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Test and preview all reusable UI components
          </span>
        </div>
      </section>

      <ConfirmDialog
        open={confirmOpen}
        title="Revoke admin privileges?"
        message={
          toRevoke
            ? `Are you sure you want to remove ${toRevoke.name} as an admin? This action can be reversed by re-adding them.`
            : ""
        }
        onClose={cancelRevoke}
        onConfirm={confirmRevoke}
      />
    </div>
  );
}
